import { useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom"; // Import createPortal
import Avatar from "../inputs/Avatar";
import { useTranslation } from "react-i18next";
import Checkbox from "../inputs/Checkbox";
import { calculateDurationInHoursAndMin } from "../../utils/serviceUtilities";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import LearningPathCardImage from "./LearningPathCardImage";

const DiscoverCourseCard = ({
  imageUrl,
  cousreName,
  courseTitle,
  courseTag,
  color,
  avatarUrl,
  userName,
  onClick,
  onAddToPath,
  description,
  keyPoints = [],
  addLearningTrack = false,
  details,
  selectable,
  isSelected,
  courseDuration,
  courseLessons,
  promoVideoUrl,
  isPlaying,
  onPlay,
  onStop,
  className = "",
}) => {
  const { t } = useTranslation("learningPaths");
  const cardRef = useRef(null);
  const timerRef = useRef(null);
  const popoverRef = useRef(null);
  const arrowRef = useRef(null);
  const [hoverState, setHoverState] = useState({
    show: false,
    top: 0,
    left: 0,
    placement: "right",
  });

  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useLayoutEffect(() => {
    if (hoverState.show && popoverRef.current) {
      if (isMobileOpen) {
        popoverRef.current.style.top = "";
        popoverRef.current.style.left = "";
        return;
      }

      const popover = popoverRef.current;
      const arrow = arrowRef.current;
      const rect = popover.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const padding = 10;
      let shiftY = 0;

      if (rect.top < padding) {
        shiftY = padding - rect.top;
      } else if (rect.bottom > viewportHeight - padding) {
        shiftY = viewportHeight - padding - rect.bottom;
      }

      if (shiftY !== 0) {
        popover.style.top = `${hoverState.top + shiftY}px`;
        if (arrow) {
          arrow.style.top = `calc(50% - ${shiftY}px)`;
        }
      }
    }
  }, [hoverState.show, hoverState.top, isMobileOpen]);

  const handleMouseEnter = () => {
    if (isMobileOpen) return;
    if (timerRef.current) clearTimeout(timerRef.current);

    if (
      !cardRef.current ||
      (!details && (!keyPoints || keyPoints.length === 0))
    )
      return;

    const rect = cardRef.current.getBoundingClientRect();
    const scrollLeft = window.scrollX;
    const scrollTop = window.scrollY;

    const popoverWidth = 360;
    const gap = 15;

    const modalContent =
      cardRef.current.closest(".modal-content") ||
      cardRef.current.closest(".modal-dialog");
    const boundaryRect = modalContent
      ? modalContent.getBoundingClientRect()
      : { left: 0, right: window.innerWidth };

    const spaceRight = boundaryRect.right - rect.right;

    let left = 0;
    let placement = "right";

    if (spaceRight < popoverWidth + gap) {
      placement = "left";
      left = rect.left + scrollLeft - popoverWidth;
    } else {
      left = rect.right + scrollLeft + gap;
    }

    const top = rect.top + scrollTop + rect.height / 2;
    setHoverState({
      show: true,
      top: top,
      left: left,
      placement,
    });
  };

  const handleMouseLeave = () => {
    if (isMobileOpen) return;
    timerRef.current = setTimeout(() => {
      setHoverState((prev) => ({ ...prev, show: false }));
    }, 150);
  };

  const handleMobileInfoClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (window.innerWidth >= 992) return;
    setIsMobileOpen(true);
  };

  const handlePopoverEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };
  const formatDuration = (seconds) => {
    let result = "";
    const { hours, minutes } = calculateDurationInHoursAndMin(seconds);
    if (hours > 0) result += `${hours}${t("learningPaths:hour")} : `;
    result += `${minutes} ${t("learningPaths:minutes")}`;
    return result;
  };
  return (
    <>
      <div className="discover-card-col">
        <div
          ref={cardRef}
          className={`card card-style h-100 p-0 discover-card ${isSelected ? "selected" : ""} ${className}`}
          role={onClick ? "button" : undefined}
          tabIndex={onClick ? 0 : undefined}
          onClick={onClick}
          onKeyDown={
            onClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClick(e);
                  }
                }
              : undefined
          }
          style={onClick ? { cursor: "pointer" } : undefined}
        >

          {selectable && !isPlaying && (
            <div className="checkbox" style={{ zIndex: 1 }}>
              <Checkbox checked={isSelected} />
            </div>
          )}
          {courseTag && !isPlaying && (
            <>
              <span
                className="course-label mb-1"
                style={{ backgroundColor: `${color}`, zIndex: 1 }}
              >
                {courseTag.title}
              </span>
            </>
          )}
          {promoVideoUrl ? (
            isPlaying ? (
            <div style={{ borderRadius: "12px 12px 0 0", overflow: "hidden", position: "relative" }} onClick={(e) => e.stopPropagation()}>
                <VideoPlayer url={promoVideoUrl} autoPlay />
                <button
                  className="btn btn-sm btn-light position-absolute top-0 end-0 m-2"
                  style={{ zIndex: 10, borderRadius: '50%', width: '30px', height: '30px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onStop && onStop();
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ) : (
              <div
                className="position-relative"
                style={{ cursor: "pointer" }}
              >
                <LearningPathCardImage
                  src={imageUrl}
                  alt={cousreName || courseTitle}
                  wrapperClassName="discover-card__image-wrap"
                  imageClassName="card-img-top discover-card__image"
                />
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onPlay && onPlay();
                  }}
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "rgba(0,0,0,0.6)",
                    borderRadius: "50%",
                    zIndex: 5,
                  }}
                >
                  <i className="fa fa-play text-white" style={{ fontSize: "20px" }}></i>
                </div>
              </div>
            )
          ) : (
            <LearningPathCardImage
              src={imageUrl}
              alt={cousreName || courseTitle}
              wrapperClassName="discover-card__image-wrap"
              imageClassName="card-img-top discover-card__image"
            />
          )}
          <div className="card-body position-relative">
            {selectable && (details || (keyPoints && keyPoints.length > 0)) && (
              <div
                className="mobile-info-icon info-popover-mob"
                onClick={handleMobileInfoClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <i
                  className="fa fa-info-circle"
                  style={{ fontSize: "18px" }}
                  aria-hidden="true"
                ></i>
              </div>
            )}

            <div className="discover-card__meta">
              <Avatar
                avatarUrl={avatarUrl}
                width={40}
                height={40}
                className="discover-card__avatar flex-shrink-0"
                fallback={'/images/user-empty.png'}
              />
              <div className="discover-card__info discover-card__info--stacked">
                <h5 className="card-title discover-card__title font_16 font-extrabold">
                  {courseTitle}
                </h5>
                <p className="font_13 font-color-grey discover-card__instructor">
                  {userName}
                </p>
                <ul className="list-details d-flex discover-card__details">
                  {courseLessons && (
                    <li>
                      <span className="fas fa-play-circle fa-fw font-color-grey"></span>
                      <p className="font_14 font-color-grey ms-1 mb-0">
                        {courseLessons + " " + t("learningPaths:lesson")}
                      </p>
                    </li>
                  )}
                  {courseDuration && (
                    <li>
                      <span className="fas fa-clock fa-fw font-color-grey"></span>
                      <span className="font_14 font-color-grey ms-1 mb-0">
                        <span>{formatDuration(courseDuration)}</span>
                      </span>
                    </li>
                  )}
                </ul>
              </div>
              {addLearningTrack && (
                <div className="discover-card__action flex-shrink-0">
                  <button
                    type="button"
                    className="btn btn-main-color discover-card__add-btn font-15"
                    aria-label={t("addToLearningTrack")}
                    title={t("addToLearningTrack")}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAddToPath && onAddToPath();
                    }}
                  >
                    <i className="fa fa-plus" aria-hidden="true"></i>
                    <span className="discover-card__add-btn-label">
                      {t("addToLearningTrack")}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectable &&
        (hoverState.show || isMobileOpen) &&
        (details || (keyPoints && keyPoints.length > 0)) &&
        createPortal(
          <div
            ref={popoverRef}
            className={`discover-card-popover popover-${hoverState.placement} ${isMobileOpen ? "mobile-popover" : ""}`}
            style={{
              top: isMobileOpen ? "0" : hoverState.top,
              left: isMobileOpen ? "0" : hoverState.left,
              position: isMobileOpen ? "fixed" : "absolute",
              width: isMobileOpen ? "100%" : undefined,
              height: isMobileOpen ? "100%" : undefined,
            }}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Close Button for Mobile */}
            {isMobileOpen && (
              <button
                type="button"
                className="btn-close mobile-close-btn"
                onClick={() => setIsMobileOpen(false)}
                aria-label="Close"
              ></button>
            )}

            {!isMobileOpen && <div className="arrow" ref={arrowRef}></div>}
            <div
              className="desc-text mb-0 flex-grow-1"
              style={{ overflowY: "auto" }}
            >
              {details && (
                <div className="mb-4">
                  <h3 className="font_14 font-bold mb-1">
                    {t("learningPaths:courseDetails")}
                  </h3>
                  <p className="font_14 mb-0 font-light">{details}</p>
                </div>
              )}

              {keyPoints && keyPoints.length > 0 && (
                <div>
                  <h3 className="font_14 font-bold mb-1">
                    {t("learningPaths:keyPoints")}
                  </h3>
                  <ul className="ps-3 mb-0 list-unstyled">
                    {keyPoints.map((point, index) => (
                      <li
                        key={index}
                        className="font_14 mb-2 d-flex align-items-start"
                        style={{ listStyle: "none", paddingInlineStart: "6px" }}
                      >
                        <span
                          className="flex-shrink-0 mt-2 me-2"
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            backgroundColor: "#6c757d",
                          }}
                          aria-hidden
                        />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default DiscoverCourseCard;
